import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Loader2, Plus, Package, Check, DollarSign, Info, TrendingUp, Link2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sales Product Fix Component for Clients
 * Used in Sales Error Resolution Panel to:
 * 1. Show cost prices from main products database (both regular and branded)
 * 2. Search within client's price list and add alias
 * 3. Show similar product suggestions before adding new
 * 4. Add as new product with sale price to client's price list
 */
const SalesProductFix = ({ clientCode, productName, token, onFixed }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("search"); // Start with client's list
  
  // Cost lookup state
  const [costQuery, setCostQuery] = useState("");
  const [costResults, setCostResults] = useState([]);
  const [loadingCost, setLoadingCost] = useState(false);
  const [costSearchTimer, setCostSearchTimer] = useState(null);
  
  // Client search state
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [loadingClient, setLoadingClient] = useState(false);
  const [clientSearchTimer, setClientSearchTimer] = useState(null);
  const [fixing, setFixing] = useState(false);
  
  // Similar products suggestion state
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  
  // New product state
  const [newProductName, setNewProductName] = useState("");
  const [newSellPrice, setNewSellPrice] = useState("");
  const [newUnit, setNewUnit] = useState("1*10");
  const [adding, setAdding] = useState(false);
  
  // Selected cost reference
  const [selectedCostRef, setSelectedCostRef] = useState(null);
  
  // Syncing cost state
  const [syncing, setSyncing] = useState(false);

  const wrongName = productName || "";

  // Search in main products database for cost prices
  const searchCostPrices = useCallback(async (q) => {
    if (!q || q.length < 2) { 
      setCostResults([]); 
      return; 
    }
    setLoadingCost(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/products/search-with-cost?query=${encodeURIComponent(q)}&limit=10`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setCostResults(data.results || []);
      } else {
        setCostResults([]);
      }
    } catch (e) { 
      console.error("Cost search error:", e); 
      setCostResults([]);
    }
    finally { setLoadingCost(false); }
  }, [token]);

  // Search in client's price list
  const searchClientProducts = useCallback(async (q) => {
    if (!q || q.length < 2 || !clientCode) { 
      setClientResults([]); 
      return; 
    }
    setLoadingClient(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/dp-client/${encodeURIComponent(clientCode)}/search-products?query=${encodeURIComponent(q)}&limit=10`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setClientResults(data.results || []);
      } else {
        setClientResults([]);
      }
    } catch (e) { 
      console.error("Client search error:", e); 
      setClientResults([]);
    }
    finally { setLoadingClient(false); }
  }, [clientCode, token]);

  // Fetch similar products when Add New tab is opened
  const fetchSimilarProducts = useCallback(async () => {
    if (!wrongName || !clientCode) return;
    
    setLoadingSimilar(true);
    try {
      // Try different search terms from the product name
      const words = wrongName.split(/[\s\-_]+/).filter(w => w.length >= 2);
      const firstWord = words[0] || wrongName.slice(0, 5);
      
      const res = await fetch(
        `${BACKEND_URL}/api/dp-client/${encodeURIComponent(clientCode)}/search-products?query=${encodeURIComponent(firstWord)}&limit=5`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSimilarProducts(data.results || []);
      }
    } catch (e) {
      console.error("Similar search error:", e);
    }
    finally { setLoadingSimilar(false); }
  }, [wrongName, clientCode, token]);

  // Load similar products when switching to Add New tab
  useEffect(() => {
    if (activeTab === "new" && similarProducts.length === 0) {
      fetchSimilarProducts();
    }
  }, [activeTab, fetchSimilarProducts, similarProducts.length]);

  if (!wrongName || !clientCode) return null;

  const handleCostSearchChange = (val) => {
    setCostQuery(val);
    if (costSearchTimer) clearTimeout(costSearchTimer);
    setCostSearchTimer(setTimeout(() => searchCostPrices(val), 300));
  };

  const handleClientSearchChange = (val) => {
    setClientQuery(val);
    if (clientSearchTimer) clearTimeout(clientSearchTimer);
    setClientSearchTimer(setTimeout(() => searchClientProducts(val), 300));
  };

  // Select a cost reference to help set sell price
  const handleSelectCostRef = (product) => {
    setSelectedCostRef(product);
    setNewProductName(wrongName);
    // Suggest sell price as cost + 20% margin
    const baseCost = Math.max(product.regular_cost || 0, product.branded_cost || 0);
    if (baseCost > 0) {
      setNewSellPrice(Math.round(baseCost * 1.2).toString());
    }
    setActiveTab("new");
    toast.info(`Cost reference: ₹${baseCost} - Suggested sell price set`);
  };

  // Sync cost price from main products database to resolve the error
  const handleSyncCost = async (product) => {
    setSyncing(true);
    try {
      // Use the bulk resolve endpoint or a direct update
      const baseCost = Math.max(product.regular_cost || 0, product.branded_cost || 0);
      if (!baseCost || baseCost <= 0) {
        toast.error("No valid cost price found for this product");
        return;
      }
      
      const res = await fetch(
        `${BACKEND_URL}/api/errors/sales/resolve-cost`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            product_name: wrongName,
            client_code: clientCode,
            force_cost: baseCost
          })
        }
      );
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`Cost synced: ₹${baseCost} | Profit: ₹${data.profit?.toFixed(2) || 'N/A'}`);
        setOpen(false);
        onFixed?.();
      } else {
        toast.error(data.detail || "Failed to sync cost");
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSyncing(false);
    }
  };

  // Apply alias fix
  const handleApplyAlias = async (product) => {
    setFixing(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/dp-client/${encodeURIComponent(clientCode)}/add-product-alias`, 
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ 
            product_id: product.id, 
            wrong_name: wrongName,
            fix_orders: true
          })
        }
      );
      const data = await res.json();
      if (res.ok) {
        let msg = `Linked! "${wrongName}" → "${data.correct_name}" (₹${data.sell_price})`;
        if (data.orders_fixed > 0) {
          msg += ` | ${data.orders_fixed} orders fixed`;
        }
        toast.success(msg);
        setOpen(false);
        onFixed?.();
      } else { 
        toast.error(data.detail || "Link failed"); 
      }
    } catch (e) { 
      toast.error(e.message); 
    }
    finally { setFixing(false); }
  };

  // Add as new product to client's ledger
  const handleAddNewProduct = async () => {
    if (!newProductName.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!newSellPrice || isNaN(parseFloat(newSellPrice))) {
      toast.error("Valid sell price is required");
      return;
    }
    
    setAdding(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/dp-client/${encodeURIComponent(clientCode)}/add-new-product`, 
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ 
            product_name: newProductName.trim(),
            sell_price: parseFloat(newSellPrice),
            unit: newUnit,
            fix_orders: true
          })
        }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(
          `Added "${newProductName}" to ${clientCode}'s price list (₹${newSellPrice})` +
          (data.orders_fixed > 0 ? ` | ${data.orders_fixed} orders fixed` : "")
        );
        setOpen(false);
        onFixed?.();
      } else { 
        toast.error(data.detail || "Add failed"); 
      }
    } catch (e) { 
      toast.error(e.message); 
    }
    finally { setAdding(false); }
  };

  // Initialize on open
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      setNewProductName(wrongName);
      setNewSellPrice("");
      setSelectedCostRef(null);
      setSimilarProducts([]);
      setActiveTab("search"); // Start with client's list to encourage linking
      
      // Start search with first word
      const firstWord = wrongName.split(/[\s\-_]+/)[0];
      if (firstWord && firstWord.length >= 2) {
        setCostQuery(firstWord);
        setClientQuery(firstWord);
        searchCostPrices(firstWord);
        searchClientProducts(firstWord);
      }
    }
  };

  // Render alias badges for a product
  const renderAliases = (product) => {
    const aliases = [];
    for (let i = 1; i <= 5; i++) {
      const alias = product[`alias_${i}`];
      if (alias) aliases.push(alias);
    }
    if (aliases.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {aliases.slice(0, 3).map((a, i) => (
          <Badge key={i} variant="outline" className="text-[9px] px-1 py-0 bg-purple-50 text-purple-600 border-purple-200">
            {a.length > 15 ? a.slice(0, 15) + '...' : a}
          </Badge>
        ))}
        {aliases.length > 3 && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 bg-slate-50">
            +{aliases.length - 3} more
          </Badge>
        )}
      </div>
    );
  };

  return (
    <>
      <Button 
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-6 text-[10px] text-green-600 hover:text-green-800 hover:bg-green-50 border-green-200 gap-1"
        data-testid="sales-product-fix-btn"
      >
        <Plus className="w-3 h-3" />
        Add to Ledger
      </Button>
      
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0">
          <DialogHeader className="bg-gradient-to-r from-slate-50 to-green-50 px-4 py-3 border-b">
            <DialogTitle className="text-sm font-medium">
              <span className="text-slate-600">Product:</span>{" "}
              <span className="text-red-600">{wrongName.length > 30 ? wrongName.slice(0, 30) + '...' : wrongName}</span>
            </DialogTitle>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Client: <span className="font-semibold text-green-700">{clientCode}</span>
              <span className="text-slate-400 ml-2">• Add to client's price list</span>
            </p>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-3 h-9 rounded-none border-b bg-slate-50">
              <TabsTrigger value="search" className="text-[11px] gap-1.5 h-8 data-[state=active]:bg-white">
                <Search className="w-3.5 h-3.5" /> Client's List
              </TabsTrigger>
              <TabsTrigger value="cost" className="text-[11px] gap-1.5 h-8 data-[state=active]:bg-white">
                <DollarSign className="w-3.5 h-3.5" /> Cost Lookup
              </TabsTrigger>
              <TabsTrigger value="new" className="text-[11px] gap-1.5 h-8 data-[state=active]:bg-white">
                <Plus className="w-3.5 h-3.5" /> Add New
              </TabsTrigger>
            </TabsList>

            {/* Search Client's List Tab - PRIMARY */}
            <TabsContent value="search" className="p-3 mt-0">
              <div className="bg-amber-50 rounded-lg p-2 mb-3 border border-amber-100">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-amber-800">
                    <strong>Recommended:</strong> Search for similar products in {clientCode}'s list first. 
                    Link to an existing product to add "{wrongName}" as an alias name.
                  </p>
                </div>
              </div>
              
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                <Input 
                  className="pl-8 h-9 text-xs" 
                  placeholder={`Search in ${clientCode}'s price list...`}
                  value={clientQuery} 
                  onChange={(e) => handleClientSearchChange(e.target.value)}
                  autoFocus
                />
              </div>
              
              {loadingClient && (
                <div className="flex items-center gap-2 py-4 text-xs text-slate-500 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />Searching {clientCode}'s products...
                </div>
              )}
              
              {!loadingClient && clientResults.length === 0 && clientQuery.length >= 2 && (
                <div className="text-center py-6">
                  <Package className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">No similar products found in {clientCode}'s list</p>
                  <p className="text-[10px] text-slate-400 mt-1">Try different keywords or use "Add New" tab</p>
                </div>
              )}
              
              {clientResults.length > 0 && (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {clientResults.map((p, i) => (
                    <div 
                      key={p.id || i} 
                      className="bg-white rounded-lg px-3 py-2 border border-slate-200 hover:bg-green-50 hover:border-green-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-xs min-w-0 flex-1">
                          <span className="font-medium block text-slate-800">{p.product_name}</span>
                          <span className="text-slate-500 text-[11px]">
                            Sell: <span className="font-semibold text-green-600">₹{p.sell_price || 0}</span> | {p.unit || "N/A"}
                          </span>
                          {renderAliases(p)}
                        </div>
                        <Button 
                          size="sm" 
                          variant="default" 
                          className="h-7 text-[11px] bg-green-600 hover:bg-green-700 px-3 shrink-0 gap-1.5" 
                          disabled={fixing} 
                          onClick={() => handleApplyAlias(p)}
                        >
                          {fixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
                          Link as Alias
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-[10px] text-slate-400 mt-3 pt-2 border-t border-slate-100">
                <strong>Linking</strong> will add "{wrongName}" as an alias to the selected product (up to 5 aliases per product)
              </p>
            </TabsContent>

            {/* Cost Reference Tab */}
            <TabsContent value="cost" className="p-3 mt-0">
              <div className="bg-blue-50 rounded-lg p-2 mb-3 border border-blue-100">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-blue-700">
                    <strong>Found a match?</strong> Click "Sync Cost" to resolve the error directly, or "Use Price" to set a sell price.
                  </p>
                </div>
              </div>
              
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                <Input 
                  className="pl-8 h-9 text-xs" 
                  placeholder="Search products for cost reference..."
                  value={costQuery} 
                  onChange={(e) => handleCostSearchChange(e.target.value)} 
                />
              </div>
              
              {loadingCost && (
                <div className="flex items-center gap-2 py-4 text-xs text-slate-500 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />Looking up cost prices...
                </div>
              )}
              
              {!loadingCost && costResults.length === 0 && costQuery.length >= 2 && (
                <div className="text-center py-6">
                  <Package className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No products found in main database</p>
                </div>
              )}
              
              {costResults.length > 0 && (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {costResults.map((p, i) => {
                    const baseCost = Math.max(p.regular_cost || 0, p.branded_cost || 0);
                    return (
                    <div 
                      key={p.id || i} 
                      className="bg-white rounded-lg px-3 py-2 border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-xs min-w-0 flex-1">
                          <span className="font-medium block truncate text-slate-800">{p.product_name}</span>
                          {p.generic_name && (
                            <span className="text-[10px] text-slate-400 block truncate">{p.generic_name}</span>
                          )}
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          {/* Sync Cost button - directly resolves the error */}
                          {baseCost > 0 && (
                            <Button 
                              size="sm" 
                              variant="default" 
                              className="h-7 text-[11px] bg-green-600 hover:bg-green-700 px-2.5 gap-1" 
                              onClick={() => handleSyncCost(p)}
                              disabled={syncing}
                            >
                              {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Sync Cost
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-[11px] text-blue-600 hover:text-blue-800 hover:bg-blue-100 px-2 gap-1" 
                            onClick={() => handleSelectCostRef(p)}
                          >
                            <TrendingUp className="w-3 h-3" />
                            Use Price
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-slate-500">Regular:</span>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${p.regular_cost > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-400'}`}>
                            ₹{p.regular_cost || 0}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-slate-500">Branded:</span>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${p.branded_cost > 0 ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-50 text-slate-400'}`}>
                            ₹{p.branded_cost || 0}
                          </Badge>
                        </div>
                        {p.unit && (
                          <span className="text-[10px] text-slate-400">| {p.unit}</span>
                        )}
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </TabsContent>

            {/* Add New Tab */}
            <TabsContent value="new" className="p-3 mt-0 space-y-3">
              {/* Similar products suggestion */}
              {(loadingSimilar || similarProducts.length > 0) && (
                <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <p className="text-[11px] font-medium text-amber-800">
                      Similar products found - consider linking instead:
                    </p>
                  </div>
                  {loadingSimilar ? (
                    <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
                      <Loader2 className="w-3 h-3 animate-spin" />Checking for similar products...
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {similarProducts.slice(0, 3).map((p, i) => (
                        <div key={p.id || i} className="flex items-center justify-between bg-white/50 rounded px-2 py-1.5">
                          <span className="text-xs text-slate-700">{p.product_name}</span>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-5 text-[10px] text-amber-700 hover:bg-amber-100 px-2"
                            onClick={() => handleApplyAlias(p)}
                            disabled={fixing}
                          >
                            Link
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Cost reference info if selected */}
              {selectedCostRef && (
                <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-100">
                  <p className="text-[10px] text-blue-700 font-medium">Cost Reference Selected:</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-slate-700">{selectedCostRef.product_name}</span>
                    <Badge className="text-[10px] bg-green-100 text-green-700">
                      Cost: ₹{Math.max(selectedCostRef.regular_cost || 0, selectedCostRef.branded_cost || 0)}
                    </Badge>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-[11px] text-slate-600">Product Name</Label>
                <Input 
                  className="h-9 text-xs mt-1" 
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                  placeholder="e.g., MODAVIGIL 200 MG"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-slate-600">Sell Price (₹) *</Label>
                  <Input 
                    className="h-9 text-xs mt-1" 
                    type="number"
                    step="0.01"
                    value={newSellPrice}
                    onChange={(e) => setNewSellPrice(e.target.value)}
                    placeholder="e.g., 85"
                  />
                  {selectedCostRef && (
                    <p className="text-[10px] text-green-600 mt-0.5">
                      Suggested: ₹{Math.round(Math.max(selectedCostRef.regular_cost || 0, selectedCostRef.branded_cost || 0) * 1.2)}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[11px] text-slate-600">Unit</Label>
                  <Input 
                    className="h-9 text-xs mt-1" 
                    value={newUnit}
                    onChange={(e) => setNewUnit(e.target.value)}
                    placeholder="e.g., 1*10"
                  />
                </div>
              </div>
              
              <Button 
                className="w-full h-9 text-xs bg-green-600 hover:bg-green-700 gap-1.5"
                onClick={handleAddNewProduct}
                disabled={adding || !newProductName.trim() || !newSellPrice}
              >
                {adding ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding...</>
                ) : (
                  <><Plus className="w-3.5 h-3.5" /> Add to {clientCode}'s Price List</>
                )}
              </Button>
              
              <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
                This will add the product to {clientCode}'s ledger and resolve sales errors
              </p>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SalesProductFix;
